import { baglantiSemasi } from "@koclukpanel/contracts";
import type { GorevIcerigi, ProgramGorevi } from "./api";

/**
 * GÖREVİN KAYNAK VE BAĞLANTI ALANLARI — F-SCH-03 / F-SCH-03b
 *
 * "Görev ekle" ve "Düzenle" formları bu alanları PAYLAŞIYOR. Dönüşüm
 * (boş → `null`, sayı ayrıştırma, yalnızca değişeni gönderme) iki formda
 * ayrı yazılsaydı biri boş bağlantıyı `""` olarak gönderirdi ve sunucu
 * onu geçersiz adres diye reddederdi.
 *
 * Saf modül: React yok, test altyapısız çalışır.
 */
export interface IcerikDegeri {
  kaynakId: string;
  testHedefi: string;
  youtube: string;
  videoBasligi: string;
  konuPdf: string;
  testPdf: string;
}

export const BOS_ICERIK: IcerikDegeri = {
  kaynakId: "",
  testHedefi: "",
  youtube: "",
  videoBasligi: "",
  konuPdf: "",
  testPdf: "",
};

export function icerikGorevden(g: ProgramGorevi): IcerikDegeri {
  return {
    kaynakId: g.studentResourceBookId ?? "",
    testHedefi: g.targetTestCount === null ? "" : String(g.targetTestCount),
    youtube: g.youtubeUrl ?? "",
    videoBasligi: g.videoTitle ?? "",
    konuPdf: g.topicPdfUrl ?? "",
    testPdf: g.testPdfUrl ?? "",
  };
}

function bosNull(d: string): string | null {
  const k = d.trim();
  return k.length === 0 ? null : k;
}

/**
 * Bağlantı geçerli mi — boş geçerli (isteğe bağlı alan).
 *
 * Kural `@koclukpanel/contracts` `baglantiSemasi`nda, sunucuyla AYNI şema
 * (Codex tur 36 B6: önce burada ayrı bir kopya vardı). Asıl kapı yine
 * sunucu; bu kontrol kullanıcıyı gönderip hata almaktan kurtarıyor.
 */
export function baglantiHatasi(d: string): string | null {
  if (d.trim().length === 0) return null;
  const s = baglantiSemasi.safeParse(d);
  return s.success ? null : (s.error.issues[0]?.message ?? "Geçerli bir bağlantı yapıştırın.");
}

/** Test hedefi: boş = tanımsız (sıfır DEĞİL); doluysa pozitif tam sayı. */
function testSayisi(d: string): number | null | undefined {
  const k = d.trim();
  if (k.length === 0) return null;
  const n = Number(k);
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

export type IcerikHatalari = Partial<Record<keyof IcerikDegeri, string>>;

export function icerikHatalari(d: IcerikDegeri): IcerikHatalari {
  const h: IcerikHatalari = {};
  if (testSayisi(d.testHedefi) === undefined) h.testHedefi = "Pozitif tam sayı olmalı.";
  for (const k of ["youtube", "konuPdf", "testPdf"] as const) {
    const hata = baglantiHatasi(d[k]);
    if (hata) h[k] = hata;
  }
  // Başlık bağlantısız anlamsız: öğrenci kartında tıklanacak bir şey olmaz.
  if (d.videoBasligi.trim() && !d.youtube.trim()) {
    h.videoBasligi = "Başlık için önce video bağlantısını yapıştırın.";
  }
  return h;
}

/** Yeni görev için gövde. Hatalı değer çağırmadan önce `icerikHatalari` ile elenmeli. */
export function icerikGovdesi(d: IcerikDegeri): GorevIcerigi {
  return {
    studentResourceBookId: d.kaynakId === "" ? null : d.kaynakId,
    targetTestCount: testSayisi(d.testHedefi) ?? null,
    youtubeUrl: bosNull(d.youtube),
    videoTitle: bosNull(d.videoBasligi),
    topicPdfUrl: bosNull(d.konuPdf),
    testPdfUrl: bosNull(d.testPdf),
  };
}

/**
 * Düzenlemede YALNIZCA DEĞİŞEN alanlar — sunucu değişmeyen alanı
 * denetim kaydına "değişti" diye yazardı (`GorevDuzenle` ile aynı kural).
 */
export function icerikFarki(g: ProgramGorevi, d: IcerikDegeri): GorevIcerigi {
  const yeni = icerikGovdesi(d);
  const fark: GorevIcerigi = {};
  if (yeni.studentResourceBookId !== g.studentResourceBookId) {
    fark.studentResourceBookId = yeni.studentResourceBookId;
  }
  if (yeni.targetTestCount !== g.targetTestCount) fark.targetTestCount = yeni.targetTestCount;
  if (yeni.youtubeUrl !== g.youtubeUrl) fark.youtubeUrl = yeni.youtubeUrl;
  if (yeni.videoTitle !== g.videoTitle) fark.videoTitle = yeni.videoTitle;
  if (yeni.topicPdfUrl !== g.topicPdfUrl) fark.topicPdfUrl = yeni.topicPdfUrl;
  if (yeni.testPdfUrl !== g.testPdfUrl) fark.testPdfUrl = yeni.testPdfUrl;
  return fark;
}
